// SRS 3.4.4 — Logs & System Tracking: audit log of every admin action
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api/axios';
import './AuditLogs.css';

const METHOD_COLORS = {
  POST: '#1ea95a',
  PUT: '#e9a23b',
  PATCH: '#e9a23b',
  DELETE: '#ef4444',
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [methodFilter, setMethodFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (methodFilter) params.method = methodFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await api.get('/admin/audit-logs', { params });
      setLogs(res.data?.data || []);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, methodFilter, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const hasActiveFilters = Boolean(methodFilter || startDate || endDate);

  const clearFilters = () => {
    setPage(1);
    setMethodFilter('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="audit-logs-page">
      <div className="audit-logs-header">
        <h2>Audit Logs</h2>
        <p>Every admin action is recorded here for system tracking.</p>
      </div>

      <div className="audit-logs-filters">
        <select
          value={methodFilter}
          onChange={(e) => { setPage(1); setMethodFilter(e.target.value); }}
        >
          <option value="">All actions</option>
          <option value="POST">Create (POST)</option>
          <option value="PUT">Update (PUT)</option>
          <option value="PATCH">Update (PATCH)</option>
          <option value="DELETE">Delete (DELETE)</option>
        </select>
        <input type="date" value={startDate} onChange={(e) => { setPage(1); setStartDate(e.target.value); }} />
        <span className="filter-sep">to</span>
        <input type="date" value={endDate} onChange={(e) => { setPage(1); setEndDate(e.target.value); }} />
        {hasActiveFilters && (
          <button type="button" className="clear-filters-btn" onClick={clearFilters}>
            Clear Filters
          </button>
        )}
      </div>

      <div className="audit-logs-table-wrap">
        <table className="audit-logs-table">
          <thead>
            <tr>
              <th>Date &amp; Time</th>
              <th>Admin</th>
              <th>Action</th>
              <th>Endpoint</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="audit-empty">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="audit-empty">No audit logs found</td></tr>
            ) : (
              logs.map((log) => (
                <tr key={log._id}>
                  <td>{new Date(log.createdAt).toLocaleString('en-IN')}</td>
                  <td>{log.adminId?.name || log.adminId?.email || '—'}</td>
                  <td>
                    <span
                      className="audit-method"
                      style={{ background: METHOD_COLORS[log.method] || '#64748b' }}
                    >
                      {log.method}
                    </span>
                  </td>
                  <td className="audit-path">{log.path}</td>
                  <td>{log.statusCode || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="audit-logs-pagination">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
        <span>Page {page} of {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
      </div>
    </div>
  );
};

export default AuditLogs;
